/**
 * The watermark from scripts/watermark.py, running in the browser.
 *
 * Deliberately a straight port, constant for constant, so a photo watermarked
 * in the panel and the same photo watermarked by the desktop script come out
 * looking the same. The numbers below are the script's; changing one here
 * without changing it there is how the two drift apart.
 *
 * Nothing in this file touches the network. Photos are read from the realtor's
 * own disk, composited on a canvas, and written back to disk — S3 is not
 * involved and never sees them.
 */

/** Watermark width as a fraction of the photo's, landscape and portrait. */
export const WIDTH_RATIO = 0.5;
export const WIDTH_RATIO_VERTICAL = 0.7;

/** 0–255, as in the script: 35 is a touch under 14%. */
export const OPACITY = 35;

/** Re-encode quality for formats that have one. PIL used 95 for JPEG. */
export const JPEG_QUALITY = 0.95;

const BY_EXTENSION = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

export const SUPPORTED_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

export function extensionOf(name) {
  const match = /\.([A-Za-z0-9]+)$/.exec(String(name || ""));
  return match ? match[1].toLowerCase() : "";
}

export function isSupportedImage(name) {
  return SUPPORTED_EXTENSIONS.indexOf(extensionOf(name)) !== -1;
}

/** Does this name already look like one of our copies? */
export function looksWatermarked(name, suffix) {
  const mark = suffix || "-wm";
  const dot = String(name).lastIndexOf(".");
  const stem = dot > 0 ? String(name).slice(0, dot) : String(name);
  return stem.slice(-mark.length) === mark;
}

/** "IMG_01.jpg" -> "IMG_01-wm.jpg" */
export function copyName(name, suffix) {
  const mark = suffix || "-wm";
  const dot = String(name).lastIndexOf(".");
  if (dot <= 0) return name + mark;
  return name.slice(0, dot) + mark + name.slice(dot);
}

/** The watermark artwork, decoded once and reused for the whole batch. */
export function loadWatermark(src) {
  return new Promise(function (resolve, reject) {
    const image = new Image();
    image.onload = function () {
      resolve(image);
    };
    image.onerror = function () {
      reject(new Error("Не вдалося завантажити зображення водяного знака"));
    };
    image.src = src;
  });
}

/**
 * Decode a photo at its true on-screen orientation.
 *
 * PIL ignores the EXIF orientation tag, so the script watermarks the raw pixel
 * grid; a phone photo stored landscape-plus-"rotate 90" is treated as
 * landscape and gets the 0.5 ratio even though everyone sees a portrait. The
 * browser applies the tag, which is the orientation the realtor is actually
 * looking at — so this picks the ratio the photo looks like, and writes the
 * rotation into the pixels rather than leaving it in a tag the next program
 * may or may not honour.
 */
function decode(blob) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(blob, { imageOrientation: "from-image" });
  }
  // Safari without createImageBitmap options: <img> applies orientation too.
  return new Promise(function (resolve, reject) {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = function () {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = function () {
      URL.revokeObjectURL(url);
      reject(new Error("Не вдалося прочитати зображення"));
    };
    image.src = url;
  });
}

function sizeOf(decoded) {
  return {
    width: decoded.width || decoded.naturalWidth,
    height: decoded.height || decoded.naturalHeight,
  };
}

/**
 * Composite the watermark onto one photo.
 *
 * Returns { blob, width, height, type } — the caller decides where it goes.
 */
export async function watermarkImage(blob, watermark, options) {
  const opts = options || {};
  const photo = await decode(blob);
  const size = sizeOf(photo);

  if (!size.width || !size.height) {
    throw new Error("Зображення порожнє або пошкоджене");
  }

  // Previews render the same composite at a fraction of the size. Every
  // measurement below is a fraction of the width, so a scaled-down preview is
  // an honest picture of the full-size result rather than an approximation.
  let width = size.width;
  let height = size.height;
  if (opts.maxSide && Math.max(width, height) > opts.maxSide) {
    const shrink = opts.maxSide / Math.max(width, height);
    width = Math.max(1, Math.round(width * shrink));
    height = Math.max(1, Math.round(height * shrink));
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(photo, 0, 0, width, height);

  // int() in Python truncates; Math.floor matches it for these positive values.
  const ratio = height > width ? WIDTH_RATIO_VERTICAL : WIDTH_RATIO;
  const targetWidth = Math.floor(width * ratio);
  const scale = targetWidth / watermark.width;
  const targetHeight = Math.floor(watermark.height * scale);

  if (targetWidth > 0 && targetHeight > 0) {
    // Resizing the artwork once, with a proper filter, rather than letting
    // drawImage scale it: the script resizes with LANCZOS, and drawImage's
    // default filter is visibly softer on a mark this large.
    let mark = watermark;
    let madeBitmap = false;
    if (typeof createImageBitmap === "function") {
      try {
        mark = await createImageBitmap(watermark, {
          resizeWidth: targetWidth,
          resizeHeight: targetHeight,
          resizeQuality: "high",
        });
        madeBitmap = true;
      } catch (err) {
        mark = watermark;
      }
    }

    // Python scales the alpha channel by OPACITY/255; globalAlpha does exactly
    // that to the source's alpha as it composites.
    const previous = ctx.globalAlpha;
    ctx.globalAlpha = OPACITY / 255;
    ctx.drawImage(
      mark,
      Math.floor((width - targetWidth) / 2),
      Math.floor((height - targetHeight) / 2),
      targetWidth,
      targetHeight
    );
    ctx.globalAlpha = previous;

    if (madeBitmap && mark.close) mark.close();
  }

  if (photo.close) photo.close();

  const type = BY_EXTENSION[extensionOf(opts.name)] || "image/jpeg";
  const quality = type === "image/png" ? undefined : JPEG_QUALITY;

  const out = await new Promise(function (resolve, reject) {
    canvas.toBlob(
      function (result) {
        if (result) resolve(result);
        else reject(new Error("Не вдалося закодувати зображення"));
      },
      type,
      quality
    );
  });

  // Let the canvas go before the next photo in the batch is decoded.
  canvas.width = 1;
  canvas.height = 1;

  return {
    blob: out,
    width: width,
    height: height,
    sourceWidth: size.width,
    sourceHeight: size.height,
    type: type,
  };
}
